/* eslint-disable no-mixed-operators */
/* NOTE:
 * #ppt_x+(cos(-2*pi*(1-$))*-#ppt_x-sin(-2*pi*(1-$))*(1-#ppt_y))*(1-$) $ ∈ [0,1]
 * #ppt_y+(sin(-2*pi*(1-$))*-#ppt_x+cos(-2*pi*(1-$))*(1-#ppt_y))*(1-$) $ ∈ [0,1]
 * 旋转飞入动画 x、y 轨迹为上面列出的较为复杂的三角函数，
 * 考虑动画过程中计算开销并参考与之对应「旋转飞出」动画和「中心旋转」动画，
 * 将其处理成 20 个关键帧，并对其中三角函数部分预先做一个粗略计算，
 * 使其路径基本拟合原三角函数定义时的轨迹
 */

import { presetClassTypes } from '@src/slide/domain/ui-model/element-modeles/property/animation/constant';
import { AnimType } from '@tencent/slide-sdk-model';
import { getAnimDuration, getSlideWidthAndHeight, isRightAnimType } from '../utils';


// 入场 x 偏移轨迹
const entrXTavList = (x: number, y: number, dur: number, width: number) => {
    // x + (cos(-2π*(1-$))* -x - sin(-2π*(1-$))*(1-y))*(1-$)
    const calcList = [
        0.95 * (-x * 0.9511 - (width - y) * 0.3090),
        0.90 * (-x * 0.8090 - (width - y) * 0.5878),
        0.85 * (-x * 0.5878 - (width - y) * 0.8090),
        0.80 * (-x * 0.3090 - (width - y) * 0.9511),
        0.75 * (-x * -0.0000 - (width - y) * 1.0000),
        0.70 * (-x * -0.3090 - (width - y) * 0.9511),
        0.65 * (-x * -0.5878 - (width - y) * 0.8090),
        0.60 * (-x * -0.8090 - (width - y) * 0.5878),
        0.55 * (-x * -0.9511 - (width - y) * 0.3090),
        0.50 * (-x * -1.0000 - (width - y) * -0.0000),
        0.45 * (-x * -0.9511 - (width - y) * -0.3090),
        0.40 * (-x * -0.8090 - (width - y) * -0.5878),
        0.35 * (-x * -0.5878 - (width - y) * -0.8090),
        0.30 * (-x * -0.3090 - (width - y) * -0.9511),
        0.25 * (-x * 0.0000 - (width - y) * -1.0000),
        0.20 * (-x * 0.3090 - (width - y) * -0.9511),
        0.15 * (-x * 0.5878 - (width - y) * -0.8090),
        0.10 * (-x * 0.8090 - (width - y) * -0.5878),
        0.05 * (-x * 0.9511 - (width - y) * -0.3090),
        0 * (-x * 1.0000 + (width - y) * 0.0000),
    ];
    const duration = 1 / calcList.length * dur;
    return calcList.map(value => ({ value, duration }));
};

// 入场 y 偏移轨迹
const entrYTavList = (x: number, y: number, dur: number, height: number) => {
    // y + (sin(-2π*(1-$))* -x - cos(-2π*(1-$))*(1-y))*(1-$)
    const calcList = [
        0.95 * (-x * 0.3090 + (height - y) * 0.9511),
        0.90 * (-x * 0.5878 + (height - y) * 0.8090),
        0.85 * (-x * 0.8090 + (height - y) * 0.5878),
        0.80 * (-x * 0.9511 + (height - y) * 0.3090),
        0.75 * (-x * 1.0000 + (height - y) * -0.0000),
        0.70 * (-x * 0.9511 + (height - y) * -0.3090),
        0.65 * (-x * 0.8090 + (height - y) * -0.5878),
        0.60 * (-x * 0.5878 + (height - y) * -0.8090),
        0.55 * (-x * 0.3090 + (height - y) * -0.9511),
        0.50 * (-x * -0.0000 + (height - y) * -1.0000),
        0.45 * (-x * -0.3090 + (height - y) * -0.9511),
        0.40 * (-x * -0.5878 + (height - y) * -0.8090),
        0.35 * (-x * -0.8090 + (height - y) * -0.5878),
        0.30 * (-x * -0.9511 + (height - y) * -0.3090),
        0.25 * (-x * -1.0000 + (height - y) * 0.0000),
        0.20 * (-x * -0.9511 + (height - y) * 0.3090),
        0.15 * (-x * -0.8090 + (height - y) * 0.5878),
        0.10 * (-x * -0.5878 + (height - y) * 0.8090),
        0.05 * (-x * -0.3090 + (height - y) * 0.9511),
        0 * (x * 0.0000 - (height - y) * 1.0000),
    ];

    const duration = 1 / calcList.length * dur;
    return calcList.map(value => ({ value, duration }));
};

// 退场 x 偏移轨迹
const exitXTavList = (x: number, y: number, dur: number, width: number) => {
    const calcList = [
        -0.0500 * (x * 0.9511 + (width - y) * 0.3090),
        -0.1000 * (x * 0.8090 + (width - y) * 0.5878),
        -0.1500 * (x * 0.5878 + (width - y) * 0.8090),
        -0.2000 * (x * 0.3090 + (width - y) * 0.9511),
        -0.2500 * (x * -0.0000 + (width - y) * 1.0000),
        -0.3000 * (x * -0.3090 + (width - y) * 0.9511),
        -0.3500 * (x * -0.5878 + (width - y) * 0.8090),
        -0.4000 * (x * -0.8090 + (width - y) * 0.5878),
        -0.4500 * (x * -0.9511 + (width - y) * 0.3090),
        -0.5000 * (x * -1.0000 + (width - y) * -0.0000),
        -0.5500 * (x * -0.9511 + (width - y) * -0.3090),
        -0.6000 * (x * -0.8090 + (width - y) * -0.5878),
        -0.6500 * (x * -0.5878 + (width - y) * -0.8090),
        -0.7000 * (x * -0.3090 + (width - y) * -0.9511),
        -0.7500 * (x * 0.0000 + (width - y) * -1.0000),
        -0.8000 * (x * 0.3090 + (width - y) * -0.9511),
        -0.8500 * (x * 0.5878 + (width - y) * -0.8090),
        -0.9000 * (x * 0.8090 + (width - y) * -0.5878),
        -0.9500 * (x * 0.9511 + (width - y) * -0.3090),
        -1.0000 * (x * 1.0000 + (width - y) * 0.0000),
    ];
    const duration = 1 / calcList.length * dur;
    return calcList.map(value => ({ value, duration }));
};

// 退场 y 偏移轨迹
const exitYTavList = (x: number, y: number, dur: number, height: number) => {
    const calcList = [
        -0.0500 * (x * 0.3090 - (height - y) * 0.9511),
        -0.1000 * (x * 0.5878 - (height - y) * 0.8090),
        -0.1500 * (x * 0.8090 - (height - y) * 0.5878),
        -0.2000 * (x * 0.9511 - (height - y) * 0.3090),
        -0.2500 * (x * 1.0000 - (height - y) * -0.0000),
        -0.3000 * (x * 0.9511 - (height - y) * -0.3090),
        -0.3500 * (x * 0.8090 - (height - y) * -0.5878),
        -0.4000 * (x * 0.5878 - (height - y) * -0.8090),
        -0.4500 * (x * 0.3090 - (height - y) * -0.9511),
        -0.5000 * (x * -0.0000 - (height - y) * -1.0000),
        -0.5500 * (x * -0.3090 - (height - y) * -0.9511),
        -0.6000 * (x * -0.5878 - (height - y) * -0.8090),
        -0.6500 * (x * -0.8090 - (height - y) * -0.5878),
        -0.7000 * (x * -0.9511 - (height - y) * -0.3090),
        -0.7500 * (x * -1.0000 - (height - y) * 0.0000),
        -0.8000 * (x * -0.9511 - (height - y) * 0.3090),
        -0.8500 * (x * -0.8090 - (height - y) * 0.5878),
        -0.9000 * (x * -0.5878 - (height - y) * 0.8090),
        -0.9500 * (x * -0.3090 - (height - y) * 0.9511),
        -1.0000 * (x * 0.0000 - (height - y) * 1.0000),
    ];
    const duration = 1 / calcList.length * dur;
    return calcList.map(value => ({ value, duration }));
};

// 入场缩放
const entrScaleList = (len: number, dur: number) => new Array(len).fill(0)
    .map((_, i) => ({
        value: i / len,
        duration: dur / len,
    }));

// 退场缩放
const exitScaleList = (len: number, dur: number) => new Array(len).fill(0)
    .map((_, i) => ({
        value: 1 - (i / len),
        duration: dur / len,
    }));

export const getSpiralToStype = (timeNode, playMode, animClass = presetClassTypes.ENTR) => {
    if (!isRightAnimType(timeNode, playMode, [AnimType.SpiralIn, AnimType.SpiralOut])) return;
    const { slideWidth, slideHeight } = getSlideWidthAndHeight(timeNode, playMode);
    const x = timeNode.elementModel.props.style.left;
    const y = timeNode.elementModel.props.style.top;
    const isEntr = animClass === presetClassTypes.ENTR;
    const duration = getAnimDuration(timeNode, playMode);
    const translateX = isEntr ? entrXTavList(x, y, duration, slideWidth) : exitXTavList(x, y, duration, slideWidth);
    const translateY = isEntr ? entrYTavList(x, y, duration, slideHeight) : exitYTavList(x, y, duration, slideHeight);
    const len = translateX.length || translateY.length;
    const scale = isEntr ? entrScaleList(len, duration) : exitScaleList(len, duration);

    return {
        scaleX: scale,
        scaleY: scale,
        translateX,
        translateY,
    };
};
